/**
 * Active Processes Subscription Policy
 *
 * Subscribes to BatchProcesses readmodel (non-archived)
 * and keeps the shared batch processes store up to date. Detects stale processes
 * (>30s since last updatedAt) and marks them as errored. Auto-removes completed
 * processes that have no errors.
 *
 * Similar to publications batchProcessPolicy but operates on the shared store
 * with a 30s stale check interval.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { BatchProcessesSharedState } from '../state';
import { BatchProcessesSharedCommand } from '../commands';
import { BatchProcessesReadmodelType } from '@bett3r-dev/teselly-domain';

const STALE_CHECK_INTERVAL_MS = 30_000;

export const createActiveProcessesSubscriptionPolicy = (
  backend: ClientLibraryType
): Policy<BatchProcessesSharedState, BatchProcessesSharedCommand> => ({
  name: 'active-processes-subscription',

  init: ({ dispatch, getState }) => {
    let staleCheckInterval: ReturnType<typeof setInterval> | null = null;

    /**
     * Detect stale processes: if updatedAt is older than 30s, mark as errored.
     * Mirrors validateBatchProcessProgress pattern from publications batchProcessPolicy.
     */
    const validateBatchProcessProgress = () => {
      const { activeProcesses } = getState();
      const now = Date.now();

      for ( const process of activeProcesses ) {
        if ( new Date( process.updatedAt ).getTime() + STALE_CHECK_INTERVAL_MS < now ) {
          dispatch(
            {
              type: 'batchProcesses/shared/subscription/upsertProcess',
              payload: { process: { ...process, status: 'errored' }}
            },
            { source: 'api' }
          );
        }
      }
    };

    /**
     * Process an incoming batch process event.
     * - Archived processes are removed (manual archive → hide).
     * - All others are upserted into state.
     *
     * TV1-1979 (§2.6): completion-based auto-removal is NOT done here. The
     * uniform, convergence-aware removal criterion is owned solely by
     * `activeProgressSubscriptionPolicy` (`shouldAutoRemoveProcess`), so this
     * policy no longer keeps a parallel `completedCount === totalItems` copy.
     */
    const handleBatchProcessEvent = ( data: BatchProcessesReadmodelType ) => {
      const isArchived = data.isArchived;

      if ( isArchived ) {
        dispatch(
          { type: 'batchProcesses/shared/subscription/removeProcess', payload: { processId: data.id }},
          { source: 'api' }
        );

        // If no more processes remain after this removal, clear the interval
        const remaining = getState().activeProcesses.filter( p => p.id !== data.id );
        if ( remaining.length === 0 && staleCheckInterval ) {
          clearInterval( staleCheckInterval );
          staleCheckInterval = null;
        }
        return;
      }

      // Ensure the stale-check interval is running
      if ( !staleCheckInterval ) {
        staleCheckInterval = setInterval(() => {
          validateBatchProcessProgress();
        }, STALE_CHECK_INTERVAL_MS );
      }

      dispatch(
        { type: 'batchProcesses/shared/subscription/upsertProcess', payload: { process: data }},
        { source: 'api' }
      );
    };

    // Subscribe to batch process readmodel changes
    const subscription = backend.subscriptions.BatchProcesses(
      {},
      ( change: any ) => {
        // Snapshot: full state initialization from subscription
        if ( change?.type === 'snapshot' ) {
          dispatch(
            { type: 'batchProcesses/shared/subscription/setProcesses', payload: { processes: change.data?.items ?? []}},
            { source: 'api' }
          );
          return;
        }

        if ( change?.type === 'delete' ) {
          // On delete, remove the process from state
          if ( change?.data?.id ) {
            dispatch(
              { type: 'batchProcesses/shared/subscription/removeProcess', payload: { processId: change.data.id }},
              { source: 'api' }
            );
          }
          return;
        }

        if ( change?.data ) {
          handleBatchProcessEvent( change.data );
        }
      }
    );

    // Run an initial stale check shortly after mount
    const initialCheckTimeout = setTimeout(() => {
      validateBatchProcessProgress();
    }, 1000 );

    // Cleanup: close subscription, clear interval and initial timeout
    return () => {
      subscription.close();
      if ( staleCheckInterval ) {
        clearInterval( staleCheckInterval );
      }
      clearTimeout( initialCheckTimeout );
    };
  }
});
